import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal, Text, Group, Button, Stack, Avatar, Badge, Loader, Center } from '@mantine/core';
import { IconUser, IconCheck, IconX, IconCreditCard, IconLogout } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { unifiedNFCService } from '../../../services/nfc/unifiedNFCService';
import { useAuth } from '../../../features/auth/contexts/AuthContext';
import { useNFC } from '../../../features/nfc/contexts/NFCContext';
import { NFCLoginModal } from './NFCLoginModal';
import type { NFCLoginStep } from '../../types';
import axios from 'axios';

interface DetectedStaff {
  id: number;
  uid: string;
  name: string;
  email: string;
  role: string;
  store_name: string;
  photo_path?: string;
}

export const NFCLoginHandler: React.FC = () => {
  const { user, nfcLogin } = useAuth();
  const { isConnected } = useNFC();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [detectedStaff, setDetectedStaff] = useState<DetectedStaff | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingCard, setIsCheckingCard] = useState(false);

  // 新しいステップ表示用のステート
  const [showStepModal, setShowStepModal] = useState(false);
  const [currentStep, setCurrentStep] = useState<NFCLoginStep>('detecting');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [authenticatedStaff, setAuthenticatedStaff] = useState<DetectedStaff | null>(null);

  useEffect(() => {
    // ログインページでは独自のNFC処理があるためスキップ
    if (location.pathname === '/login') {
      return;
    }

    // NFCが接続されていない場合はスキップ
    if (!isConnected) {
      return;
    }

    // NFCカード検出リスナー（統合NFCサービス使用）
    const unsubscribe = unifiedNFCService.on('nfc_detected', async (event) => {
      // 書き込みモード中は無視
      if (event.context === 'write') {
        return;
      }

      // NFCサービスが書き込みモード中なら無視
      if (unifiedNFCService.isWriteMode()) {
        return;
      }

      const { uid, type, entity_id } = event.data || {};

      // typeをNumberに変換（文字列で来る可能性があるため）
      const cardType = Number(type);

      // 顧客カード（type=2）の場合はスキップ（CustomerNFCHandlerで処理）
      if (cardType === 2) {
        return;
      }

      // スタッフカード（type=1）の場合のみ処理
      if (cardType === 1) {
        await handleStaffCardDetected(uid, entity_id);
      } else if (cardType === 0) {
        // 未初期化カードの場合は通知のみ
        showNotification({
          title: '未登録カード',
          message: 'このNFCカードはまだ登録されていません',
          color: 'yellow',
          icon: <IconCreditCard />,
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isConnected, user, location.pathname]);

  const handleStaffCardDetected = async (uid: string, _entityId: number) => {
    // 既に処理中の場合はスキップ
    if (isCheckingCard || isProcessing) {
      return;
    }

    // ステップ表示モーダルを開始
    setCurrentStep('detecting');
    setLoginError(null);
    setAuthenticatedStaff(null);
    setShowStepModal(true);
    setIsCheckingCard(true);

    // 検出ステップの表示時間を確保
    await new Promise(resolve => setTimeout(resolve, 800));

    try {
      // 認証ステップに移行
      setCurrentStep('authenticating');

      // NFCカードで認証を試みる
      const response = await axios.post('/v3/auth/nfc-login', {
        nfc_uid: uid,
        device_name: 'nfc-reader',
      });

      if (response.data.success) {
        const staffData = response.data.data.staff;
        const staffInfo = {
          id: staffData.id,
          uid: uid,
          name: staffData.name,
          email: staffData.email,
          role: staffData.role,
          store_name: staffData.store?.name || '未所属',
          photo_path: staffData.photo_path,
        };

        // 現在ログイン中のユーザーと異なる場合は切り替え確認
        if (user && user.id !== staffData.id) {
          // ステップモーダルを閉じて切り替え確認モーダルを表示
          setShowStepModal(false);
          setDetectedStaff(staffInfo);
          setShowModal(true);
        } else if (!user) {
          // ログインしていない場合は自動ログイン
          setAuthenticatedStaff(staffInfo);
          setCurrentStep('success');

          // 成功ステップを表示してからログイン実行
          setTimeout(async () => {
            await performLogin(response.data.data.token, staffData);
            setShowStepModal(false);
          }, 2000);
        } else {
          // 同じユーザーの場合
          setCurrentStep('success');
          setAuthenticatedStaff(staffInfo);

          showNotification({
            title: '確認',
            message: `${staffData.name}さんは既にログイン中です`,
            color: 'blue',
            icon: <IconCheck />,
          });

          // 成功表示後に閉じる
          setTimeout(() => {
            setShowStepModal(false);
          }, 2000);
        }
      }
    } catch (error: any) {
      // エラーステップに移行
      setCurrentStep('error');

      let errorMessage = 'NFCログインでエラーが発生しました';
      if (error.response?.status === 422) {
        errorMessage = '登録されていないNFCカードです';
      } else if (error.response?.status === 403) {
        errorMessage = 'このカードは現在無効化されています';
      } else if (error.response?.status === 429) {
        errorMessage = 'アクセス頻度が制限を超えました';
      }

      setLoginError(errorMessage);

      // 通知も表示
      showNotification({
        title: 'エラー',
        message: errorMessage,
        color: 'red',
        icon: <IconX />,
      });
    } finally {
      setIsCheckingCard(false);
    }
  };

  const performLogin = async (token: string, staffData: any) => {
    // AuthContextを通じてログイン
    nfcLogin(token, staffData);

    showNotification({
      title: 'ログイン成功',
      message: `${staffData.name}さん、こんにちは`,
      color: 'green',
      icon: <IconCheck />,
    });

    // ページをリロードして新しいユーザー情報を反映
    window.location.reload();
  };

  const handleSwitchUser = async () => {
    if (!detectedStaff) return;

    setIsProcessing(true);

    try {
      // 新しいユーザーでログイン（ログアウトせずに直接切り替え）
      const response = await axios.post('/v3/auth/nfc-login', {
        nfc_uid: detectedStaff.uid,
        device_name: 'nfc-reader',
      });

      if (response.data.success) {
        const { token, staff } = response.data.data;

        // 古いトークンをクリア
        const oldToken = localStorage.getItem('auth_token');
        if (oldToken) {
          try {
            // 古いセッションをログアウト（エラーは無視）
            await axios.post('/api/v3/auth/logout');
          } catch (_e) {
            // エラーを無視
          }
        }

        // AuthContextを通じて新しいユーザーを設定
        nfcLogin(token, staff);

        showNotification({
          title: 'ユーザー切り替え成功',
          message: `${staff.name}さんに切り替えました`,
          color: 'green',
          icon: <IconCheck />,
        });

        // ページをリロードして新しいユーザー情報を反映
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (_error) {
      showNotification({
        title: 'エラー',
        message: 'ユーザー切り替えに失敗しました',
        color: 'red',
        icon: <IconX />,
      });
      setIsProcessing(false);
      setShowModal(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setDetectedStaff(null);
  };

  const getRoleBadge = (role: string) => {
    const colors = {
      admin: 'red',
      manager: 'blue',
      staff: 'gray',
    };
    const labels = {
      admin: '管理者',
      manager: 'マネージャー',
      staff: 'スタッフ',
    };
    return (
      <Badge color={colors[role as keyof typeof colors] || 'gray'} variant="filled">
        {labels[role as keyof typeof labels] || role}
      </Badge>
    );
  };

  return (
    <>
      {/* 新しいステップ表示モーダル */}
      <NFCLoginModal
        opened={showStepModal}
        onClose={() => {
          if (currentStep === 'error' || currentStep === 'detecting') {
            setShowStepModal(false);
            setCurrentStep('detecting');
            setLoginError(null);
            setAuthenticatedStaff(null);
          }
        }}
        staff={authenticatedStaff}
        isProcessing={currentStep === 'authenticating'}
        loginSuccess={currentStep === 'success'}
        currentStep={currentStep}
        error={loginError}
      />

      {/* 既存のスタッフ切り替えモーダル */}
      <Modal opened={showModal} onClose={handleCancel} title="スタッフ切り替え" size="md" centered>
        <Stack>
          {detectedStaff && !isProcessing && (
            <>
              <Group>
                <Avatar src={detectedStaff.photo_path || null} size="lg" radius="md">
                  <IconUser size={30} />
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Text size="lg" fw={600}>
                    {detectedStaff.name}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {detectedStaff.email}
                  </Text>
                  <Group gap="xs" mt={4}>
                    {getRoleBadge(detectedStaff.role)}
                    <Badge variant="light">{detectedStaff.store_name}</Badge>
                  </Group>
                </div>
              </Group>

              <Text size="sm" c="dimmed">
                NFCカードが検出されました。このスタッフに切り替えますか？
              </Text>

              {user && (
                <Group
                  p="md"
                  style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '8px' }}
                >
                  <IconLogout size={20} />
                  <div>
                    <Text size="xs" c="dimmed">
                      現在のログインユーザー
                    </Text>
                    <Text size="sm" fw={500}>
                      {user.name}
                    </Text>
                  </div>
                </Group>
              )}

              <Group justify="flex-end" mt="md">
                <Button variant="light" onClick={handleCancel} disabled={isProcessing}>
                  キャンセル
                </Button>
                <Button
                  leftSection={<IconCreditCard size={18} />}
                  onClick={handleSwitchUser}
                  loading={isProcessing}
                  disabled={isProcessing}
                >
                  切り替える
                </Button>
              </Group>
            </>
          )}

          {isProcessing && (
            <Center py="xl">
              <Stack align="center">
                <Loader size="lg" />
                <Text size="sm" c="dimmed">
                  ユーザーを切り替えています...
                </Text>
              </Stack>
            </Center>
          )}
        </Stack>
      </Modal>
    </>
  );
};
